'use client'

import { useCallback, useMemo } from 'react'
import { IconDiscount2 } from '@tabler/icons-react'
import clsx from 'clsx'
import Image from 'next/image'
import Link from 'next/link'

import {
  RecommendationMetadataType,
  syneriseRecommendationClick
} from 'lib/synerise'
import { LoginModal } from 'modules/auth'
import { DateOfBirthModal } from 'modules/customer'
import { SeoListName } from 'shared/analytics'
import { Collapse } from 'shared/ui/Collapse'
import { Tooltip } from 'shared/ui/Tooltip'
import { capitalizeWords } from 'shared/utils/common'
import { isNotNilOrEmpty } from 'shared/utils/ramda'
import { addTestId } from 'shared/utils/tests'

import { selectProduct } from '../../analytics'
import useProductCard from '../../hooks/useProductCard'
import { ProductActionButton, ProductReplaceButton, ProductSpecialPromotion } from '../../ports'
import { ProductTypes } from '../../types/products'
import { getProductInsightsDataAttributes } from '../../utils/algolia'
import { isUnavailableProduct } from '../../utils/products'
import AddToFavouritesButton from '../AddToFavouritesButton'

import ProductCardBadgesList from './components/ProductCardBadgesList'
import ProductCardLabelsList from './components/ProductCardLabelsList'
import ProductCardPrices from './components/ProductCardPrices'
import ProductCardProductSets from './components/ProductCardProductSets'

import styles from './ProductCard.module.scss'

type ProductCardProps = {
  productPath: string
  product: ProductTypes
  seoButtonPlace: string
  ageLimitMetCategory?: boolean
  realProductId?: string
  isSubstitute?: boolean
  onClickSeo?: boolean
  idxOnList: number
  id: string
  listName: SeoListName
  syneriseRecommendationMetadata?: RecommendationMetadataType | null
  queryID?: string | null // Required for Algolia analytics
}

const ProductCard = ({
  productPath,
  ageLimitMetCategory,
  product,
  realProductId,
  isSubstitute,
  onClickSeo = true,
  seoButtonPlace,
  idxOnList,
  id,
  listName,
  syneriseRecommendationMetadata,
  queryID
}: ProductCardProps): React.ReactNode => {
  const {
    loginModalOpen,
    handleBlurredProductClick,
    handleCloseDobModal,
    handleCloseLoginModal,
    dobModalOpen,
    blurProduct,
    blurProductMessage,
    user,
    parsedBadges,
    name,
    nameDescriptionShort,
    nameShort,
    productImage,
    parsedTags,
    handleMouseOut,
    handleMouseOver,
    hover,
    maxQuantityForSpecialOffer,
    pricePerUnit,
    quantityJump,
    setQuantityJump,
    unitDetails,
    path,
    brandPath
  } = useProductCard({ product, ageLimitMetCategory, productPath })

  const capitalizedShortName = useMemo(
    () => capitalizeWords(nameShort),
    [nameShort]
  )

  const handleSyneriseRecommendationClickEvent = useCallback(() => {
    if (
      syneriseRecommendationMetadata?.campaignId &&
      syneriseRecommendationMetadata?.correlationId
    ) {
      syneriseRecommendationClick({
        campaignId: syneriseRecommendationMetadata.campaignId,
        correlationId: syneriseRecommendationMetadata.correlationId,
        item: product.code
      })
    }
  }, [syneriseRecommendationMetadata, product.code])

  const handleClick = useCallback(() => {
    if (onClickSeo) {
      selectProduct({
        user,
        product,
        idxOnList,
        listName
      })
    }
    handleSyneriseRecommendationClickEvent()
  }, [
    onClickSeo,
    user,
    product,
    idxOnList,
    listName,
    handleSyneriseRecommendationClickEvent
  ])

  const insightsDataAttributes = useMemo(
    () => getProductInsightsDataAttributes(queryID, product, idxOnList),
    [queryID, product, idxOnList]
  )

  const algoliaClickInsightsAttributes = queryID
    ? { 'data-insights-click-event-trigger': true } // Custom attribute to trigger Algolia insights click event in GTM.
    : undefined

  const algoliaAddToCartInsightsAttributes = queryID
    ? { 'data-insights-add-to-cart-event-trigger': true }
    : undefined

  if (isUnavailableProduct(product.nameShort)) {
    return null
  }

  const productNameId = `${id}-product-name`

  const tabIndexElements = blurProduct ? -1 : 0

  const disableHoverListenerName = (nameDescriptionShort?.length || 0) < 30

  return (
    <div className={styles.globalWrapper} id={id} {...insightsDataAttributes}>
      <LoginModal open={loginModalOpen} onClose={handleCloseLoginModal} />
      <DateOfBirthModal open={dobModalOpen} onClose={handleCloseDobModal} />
      {blurProduct && (
        <button
          className={styles.blurredIconWrapper}
          onClick={handleBlurredProductClick}
          role='button'
          aria-label={blurProductMessage}
        >
          <Image
            src='/static/UnderEighteen.svg'
            alt='poniżej 18 lat'
            width={64}
            height={64}
          />
          <div className={styles.blurredInfoWrapper}>{blurProductMessage}</div>
        </button>
      )}
      <div
        className={clsx(styles.wrapper, { [styles.blur]: blurProduct })}
        aria-describedby={productNameId}
      >
        {!blurProduct && (
          <div className={styles.favIconWrapper}>
            {isNotNilOrEmpty(parsedBadges) && (
              <ProductCardBadgesList
                rootCategorySlug={product.category?.slug}
                badges={parsedBadges}
              />
            )}
            <AddToFavouritesButton
              className={styles.rightAddToFavouritesButton}
              id='product_card_favourites_button'
              product={product}
              idxOnList={idxOnList}
              listName={listName}
              seoButtonPlace={seoButtonPlace}
            />
          </div>
        )}
        <Link
          className={styles.imageWrapper}
          href={path}
          title={name}
          tabIndex={tabIndexElements}
          onClick={handleClick}
          {...algoliaClickInsightsAttributes}
        >
          <div className={styles.specialPromotionWrapper}>
            <ProductSpecialPromotion product={product} />
          </div>
          <Image
            className={styles.styledImage}
            title={name}
            src={productImage}
            alt={name}
            fill
            sizes='(max-width: 768px) 50vw, 300px'
            {...addTestId('product-card-image')}
          />
        </Link>
        <div className={styles.productDescription}>
          <div
            className={styles.collapseWrapper}
            onMouseEnter={handleMouseOver}
            onMouseLeave={handleMouseOut}
          >
            <div className={styles.basicDetails}>
              <Link
                href={brandPath || ''}
                tabIndex={brandPath ? tabIndexElements : -1}
              >
                <h4
                  className={styles.madeBy}
                  {...addTestId('product-card-brand')}
                >
                  {capitalizedShortName}
                </h4>{' '}
              </Link>

              <Tooltip
                id={`${productNameId}-tooltip`}
                title={nameDescriptionShort}
                disableHoverListener={disableHoverListenerName}
              >
                <Link
                  href={path ?? ''}
                  title={name}
                  tabIndex={tabIndexElements}
                  onClick={handleClick}
                  {...algoliaClickInsightsAttributes}
                >
                  <h3
                    className={styles.name}
                    id={productNameId}
                    {...addTestId('product-card-name')}
                  >
                    {nameDescriptionShort || name || capitalizedShortName}
                  </h3>
                </Link>
              </Tooltip>
            </div>
            <div className={styles.grammarWrapper}>
              <p className={styles.grammar}>{unitDetails}</p>
              <p
                className={styles.pricePerGrammar}
                dangerouslySetInnerHTML={{ __html: pricePerUnit }}
                {...addTestId('product-card-price-per-unit')}
              />
            </div>
            {isNotNilOrEmpty(parsedTags) && (
              <div className={styles.tagsWrapper}>
                <ProductCardLabelsList labels={parsedTags} />
              </div>
            )}
            <ProductCardPrices
              isHovered={hover}
              product={product}
              quantityJump={quantityJump}
            />
            {isNotNilOrEmpty(maxQuantityForSpecialOffer) &&
              product.showRemainingSpecialOfferQuantity && (
                <Collapse open={hover}>
                  <div className={styles.maxPerSpecialOfferWrapper}>
                    <IconDiscount2 size={30} />
                    <p>
                      Maksymalna liczba dostępna w promocji:{' '}
                      {maxQuantityForSpecialOffer}
                    </p>
                  </div>
                </Collapse>
              )}

            <ProductCardProductSets
              isHovered={hover}
              quantityJump={quantityJump}
              product={product}
              onQuantityJumpChange={setQuantityJump}
            />
          </div>
          <div
            className={clsx(styles.buttonWrapper, 'product-thumbnail-buttons')}
          >
            {isSubstitute && !blurProduct ? ( // if they are isSubstitute, that means we operate on modal of replacements
              <ProductReplaceButton
                realProductId={realProductId} // real product
                substitute={product} // replacement
                idxOnList={idxOnList}
              />
            ) : (
              <ProductActionButton
                variant='primaryOutline'
                product={product}
                quantityJump={quantityJump}
                disabled={blurProduct}
                seoButtonPlace={seoButtonPlace}
                idxOnList={idxOnList}
                listName={listName}
                handleSyneriseRecommendationClickEvent={
                  handleSyneriseRecommendationClickEvent
                }
                algoliaInsightsAttributes={algoliaAddToCartInsightsAttributes}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductCard
